/** Mirrors Aufmass.Api CrewQuery records (camelCase JSON). Dates are yyyy-MM-dd. */

export interface CrewListItem {
  crewId: string
  name: string | null
  code: string | null
  company: string | null
  isSub: boolean
  leader: string | null
  bauleiter: string | null
  lastCheckIn: string | null
  members: number
  roster: number
  rosterLeft: number
  notOnRoster: number
  leaderNotCheckedIn: boolean
  bauleiterOutdatedInAu: boolean
  active: boolean
}

export interface CrewMember {
  employeeId: string
  name: string | null
  daysWorked: number
  lastDay: string | null
  startDate: string | null
  employed: boolean
  onRoster: boolean
  isLeader: boolean
  isNew: boolean
}

export interface CrewRosterEntry {
  employeeId: string
  name: string | null
  addedOn: string | null
  employed: boolean
  endDate: string | null
  checkedInRecently: boolean
}

export interface CrewLeaderSpan { name: string | null; from: string | null; to: string | null }
export interface CrewBauleiterChange { newBauleiter: string | null; previousBauleiter: string | null; at: string | null }

/** A project the crew has sessions on (all time). The empty uuid = sessions with no project. */
export interface CrewProject {
  projectId: string
  name: string | null
  codeInternal: string | null
  status: number | null
  crewDays: number
  metres: number
  firstWorked: string | null
  lastWorked: string | null
  leaders: string | null
  /** The project's RTK measurements by any crew — what the map would show. */
  rtkSegments: number
  /** This crew's own RTK measurements on the project. */
  crewRtkSegments: number
}

export const NO_PROJECT = '00000000-0000-0000-0000-000000000000'

export interface CrewDetail {
  crew: CrewListItem
  windowDays: number
  newWorkerDays: number
  members: CrewMember[]
  roster: CrewRosterEntry[]
  leaderHistory: CrewLeaderSpan[]
  bauleiterHistory: CrewBauleiterChange[]
  projects: CrewProject[]
}

export const WINDOWS = [7, 30, 90] as const

/** Anything an admin should look at on this crew. */
export const needsAttention = (c: CrewListItem) =>
  c.active && (c.leaderNotCheckedIn || c.notOnRoster > 0 || c.rosterLeft > 0 || c.bauleiterOutdatedInAu)

export interface CrewWeek { weekStart: string; crewDays: number; metres: number; manHours: number; companyMetresPerDay: number }
export interface CrewQuality {
  reportDays: number; repairDays: number; flaggedDays: number; noReportDays: number
  segments: number; withFix: number; meanAccuracy: number | null
}
/** 1 = best; null = not in that comparison. */
export interface CrewRanking {
  crews: number; metresPerDay: number | null; metres: number | null; days: number | null
  fix: number | null; rtkCrews: number
}
export interface CrewPosition { name: string; value: number; days: number }
export interface CrewPerformance {
  windowDays: number; reportDays: number; metres: number; manHours: number
  metresPerDay: number; companyMetresPerDay: number
  quality: CrewQuality; ranking: CrewRanking
  weeks: CrewWeek[]; positions: CrewPosition[]
}

/** ISO-8601 week number of a yyyy-MM-dd date ("KW" on the workbook). */
export function isoWeek(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  const date = new Date(Date.UTC(y!, m! - 1, d!))
  const day = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - day)
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1)
  return Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7)
}

/** One RTK measurement on the crew map (snake_case: raw JSON straight from SQL, like /api/rtk/geo). */
export interface CrewRoute {
  segment_id: string
  project_id: string | null
  project_name: string | null
  measured_at: string
  measurement_type: string | null
  material_name: string | null
  length_m: number | null
  has_rtk_fix: boolean | null
  geojson:
    | { type: 'LineString'; coordinates: [number, number][] }
    | { type: 'Point'; coordinates: [number, number] }
}

export interface CrewPhoto {
  url: string
  thumbUrl: string
  kind: 'report' | 'rtk'
  day: string | null
  project: string | null
  caption: string | null
}

export interface CrewDay { day: string; workers: number; notWorking: number; metres: number; report: boolean }
export interface CrewMonth { year: number; month: number; members: number; cameIn: number; droppedOut: number; workerDays: number }
export interface CrewTenure { employeeId: string; name: string | null; firstDay: string | null; lastDay: string | null; days: number }
export interface CrewRecorder { name: string; entries: number; isBauleiter: boolean; isLeader: boolean }
export interface CrewDiscipline {
  entries: number; sameDay: number; later: number; morning: number; workEntries: number; unclosed: number
  crewDays: number; leaderMissingDays: number; recordedBy: CrewRecorder[]
}
export interface CrewAttendance { days: CrewDay[]; months: CrewMonth[]; tenure: CrewTenure[]; discipline: CrewDiscipline }

export interface CrewBonusMonth {
  year: number; month: number; days: number; metres: number; hours: number; earnings: number
  perHour: number; normPct: number; meal: number; leaderBonus: number
  newWorkers: number; newWorkerBonus: number; total: number
}
export interface CrewBonus {
  config: { normPerHour: number; mealPerWorkerDay: number; crewLeaderBonus: number; newWorkerBonus: number; newWorkerWindowDays: number }
  months: CrewBonusMonth[]
}
